import { css } from '@emotion/react';
import { getScreen } from '@/styles/media-query';
import { withPrefix } from 'gatsby';

export type FeatureCardProps = {
  className?: string;
  index: number;
  name: string;
  head: React.ReactNode;
  textItems: string[];
  imageSrc: string;
  children: React.ReactNode;
  result: React.ReactNode;
  items: {
    imageSrc: string;
    name: string;
  }[];
  pcPosition: 'left' | 'right';
};

const FeatureCard: React.FC<FeatureCardProps> = ({
  className,
  index,
  name,
  head,
  textItems,
  imageSrc,
  children,
  result,
  items,
  pcPosition,
}) => {
  return (
    <div
      css={css`
        position: relative;
        z-index: 1;
        max-width: 1080px;
        margin-right: auto;
        margin-left: auto;
        width: 100%;
      `}
      className={className}
    >
      <div
        css={css`
          @media ${getScreen('pc')} {
            max-width: 45%;
            width: 440px;
            display: grid;
            grid-template-areas:
              '. .'
              'index name'
              'head head'
              'children children'
              'result result'
              '. .';
            grid-template-columns: 60px 1fr;
            grid-template-rows: 1fr auto auto auto auto 1fr;
            min-height: 500px;

            ${pcPosition === 'right' &&
            css`
              margin-left: auto;
            `}
          }
        `}
      >
        <p
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            font: normal normal bold 28px/51px 'Noto Sans JP', sans-serif;
            letter-spacing: 0px;
            color: #ffffff;
            width: 60px;
            height: 60px;
            border-radius: 9999px;
            position: relative;
            border: 1px solid #00b3a7;
            background: #fff;

            @media ${getScreen('pc')} {
              align-self: center;
              grid-area: index;
            }

            &::before {
              background: #00b3a7
                linear-gradient(180deg, #00b3a7 0%, #00c9be 100%) 0% 0%
                no-repeat;
              content: '';
              width: 54px;
              height: 54px;
              display: block;
              border-radius: 9999px;
              position: absolute;
              top: 2px;
              left: 2px;
            }
          `}
        >
          <span
            css={css`
              margin-bottom: 2px;
              position: relative;
            `}
          >
            {index}
          </span>
        </p>
        <p
          css={css`
            padding-left: 15px;
            margin-right: -20px;
            margin-top: -50px;
            overflow: hidden;
            position: relative;
            z-index: -1;

            @media ${getScreen('pc')} {
              padding: 0;
              margin: -40px 0 0;
              position: absolute;
              top: 0;
              left: calc(50% - 120px);

              ${pcPosition === 'right' &&
              css`
                left: initial;
                right: calc(50% - 120px);
              `}
            }
          `}
        >
          <img
            css={css`
              width: 480px;
              height: auto;

              @media ${getScreen('pc')} {
                width: 890px;
              }
            `}
            src={imageSrc}
            width="890"
            height="548"
            alt=""
          />
        </p>
        <h3
          css={css`
            font: normal normal bold 20px/32px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.8px;
            color: #00b3a7;

            @media ${getScreen('pc')} {
              grid-area: name;
              font: normal normal bold 24px/36px 'Noto Sans JP', sans-serif;
              letter-spacing: 1.66px;
              margin-left: 12px;
            }
          `}
        >
          {head}
        </h3>
        <div
          css={css`
            border-left: 1px solid #00b3a7;
            padding: 8px 0 20px 16px;
            margin: 12px 0 0 10px;

            @media ${getScreen('pc')} {
              grid-area: children;
              margin: 16px 0 0 28px;
              padding: 8px 0 20px 16px;
            }
          `}
        >
          <ul
            css={css`
              margin-bottom: 24px;
            `}
          >
            {textItems.map((item, i) => (
              <li
                key={i}
                css={css`
                  display: flex;
                  align-items: center;
                  font: normal normal bold 16px/28px 'Noto Sans JP',
                    sans-serif;
                  letter-spacing: 0.11px;
                  color: #01B3A7;
                  
                  em {
                    display: inline-flex;
                    justify-content: center;
                    align-items: center;
                    width: 48px;
                    height: 18px;
                    margin-left: 5px;
                    border-radius: 10px;
                    font: normal normal bold 10px/10px 'Noto Sans JP', sans-serif;
                    color: #ffffff;
                    letter-spacing: 0.8px;
                    background-color: #FE8321;
                  }
                `}
              >
                <i
                  css={css`
                    display: block;
                    width: 7px;
                    height: 12px;
                    margin-right: 7px;
                    margin-left: 14px;
                    background: linear-gradient(
                      180deg,
                      #00b3a7 0%,
                      #00c9be 100%
                    );
                    clip-path: path(
                      'M6.98462 6.00556C6.98401 6.28538 6.92468 6.56195 6.81047 6.81741C6.69627 7.07286 6.52973 7.3015 6.32163 7.48856L1.98763 11.3846C1.72601 11.6236 1.4048 11.7878 1.05777 11.8598C0.710752 11.9318 0.350746 11.9089 0.015625 11.7936L3.07063 6.00556L0.016625 0.205561C0.351695 0.0905626 0.711556 0.0679484 1.05839 0.140095C1.40522 0.212241 1.72621 0.376484 1.98763 0.615561L6.32163 4.51256C6.53098 4.70075 6.69826 4.93101 6.81251 5.18828C6.92676 5.44556 6.98541 5.72406 6.98462 6.00556V6.00556Z'
                    );
                  `}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div
            css={css`
              font: normal normal normal 14px/26px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.42px;
              color: #5a5b6a;

              @media ${getScreen('pc')} {
                font: normal normal normal 14px/28px 'Noto Sans JP', sans-serif;
              }
            `}
          >
            {children}
          </div>
        </div>
        <div
          css={css`
            background: #ffffff;
            border: 6px solid #00b3a71c;
            border-radius: 16px;
            padding: 14px 16px 12px;

            @media ${getScreen('pc')} {
              grid-area: result;
              padding: 20px 32px 16px;
              width: 100%;
              max-width: max-content;
            }

            strong {
              font: normal normal bold 20px/29px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.8px;
              color: #00b3a7;

              @media ${getScreen('pc')} {
                font: normal normal bold 20px/36px 'Noto Sans JP', sans-serif;
              }
            }

            small {
              font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.56px;
              color: #5a5b6a;

              @media ${getScreen('pc')} {
                font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
                letter-spacing: 1.1px;
              }
            }
            .option {
              position: absolute;
              right: 0;
              bottom: 0px;
              font: normal normal normal 12px/18px 'Noto Sans JP', sans-serif;
              color: #5A5B6A;
            }
          `}
        >
          {result}
        </div>
        <ul
          css={css`
            list-style: none;
            gap: 15px;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            margin-top: 24px;

            @media ${getScreen('pc')} {
              width: 378px;
              display: flex;
              position: absolute;
              bottom: 0;
              left: calc(50% - 20px);
              gap: 18px;
              margin: 0;

              ${pcPosition === 'right' &&
              css`
                left: initial;
                right: calc(50% - 20px);
                justify-content: flex-end;
              `}
            }
          `}
        >
          {items.map((item, i) => (
            <li
              css={css`
                background: #ffffff;
                box-shadow: 0px 4px 16px #2c28281c;
                border-radius: 8px;
                padding: 12px 8px 8px;
                gap: 3px;
                text-align: center;
                display: flex;
                flex-direction: column;
                align-items: center;

                @media ${getScreen('pc')} {
                  flex: 0 0 114px;
                  padding: 15px 8px 12px;
                  gap: 9px;
                }
              `}
              key={i}
            >
              <img
                css={css`
                  width: 50px;
                  height: auto;
                `}
                src={item.imageSrc}
                width="58"
                height="49"
                alt=""
              />
              <p
                css={css`
                  font: normal normal 500 10px/16px 'Noto Sans JP', sans-serif;
                  letter-spacing: 0.29px;
                  color: #5a5b6a;

                  ${item.name.includes('\n') &&
                  css`
                    white-space: pre-line;
                  `}

                  @media ${getScreen('pc')} {
                    font: normal normal 500 12px/16px 'Noto Sans JP', sans-serif;
                    letter-spacing: 0.14px;
                  }
                `}
              >
                {item.name}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const FeatureCard1: React.FC<Pick<FeatureCardProps, 'className'>> = ({
  className,
}) => {
  return (
    <FeatureCard
      className={className}
      index={1}
      name=""
      head={
        <>
          IPO・M&Aに向けて
          <br />
          「今」必要なことがわかる！
        </>
      }
      textItems={[
        '目的別「ナビゲーション」機能',
      ]}
      imageSrc={withPrefix('/images/features/image-1.png')}
      result={
        <p>
          <strong>IPOの経験がなくても、逆引きで必要タスクの把握が可能！</strong>
        </p>
      }
      items={[
        {
          name: 'IPOナビゲーション機能',
          imageSrc: withPrefix('/images/features/icon-1.png'),
        },
        {
          name: 'タスク\nガイドライン',
          imageSrc: withPrefix('/images/features/icon-2.png'),
        },
        {
          name: 'スケジュール\n自動生成',
          imageSrc: withPrefix('/images/features/icon-3.png'),
        },
      ]}
      pcPosition="right"
    >
      <p>
        解決したい課題から必要なタスクを逆引きできるので、次に何をすればいいのかが一目瞭然です。IPO・M&A経験者が不在でも、監査法人など外部からの依頼にスムーズに応えることができます。
      </p>
    </FeatureCard>
  );
};

export const FeatureCard6: React.FC<Pick<FeatureCardProps, 'className'>> = ({
  className,
}) => {
  return (
    <FeatureCard
      className={className}
      index={2}
      name="人事労務チェック&評価機能"
      head={
        <>
          IPO・M&A基準に対応した資本政策表作成を後押し！
        </>
      }
      textItems={[
        '資本政策表作成機能',
      ]}
      imageSrc={withPrefix('/images/features/image-6.png')}
      result={
        <p>
          <strong>
            資本政策表作成シミュレーションで根拠のある資本政策をサポート！
          </strong>
        </p>
      }
      items={[
        {
          name: '資本政策表作成\nシミュレーション',
          imageSrc: withPrefix('/images/features/icon-11.png'),
        },
      ]}
      pcPosition="left"
    >
      <p>
        資本政策のシミュレーションにより、財務戦略の解像度を高めながらIPO・M&Aに備えることができます。<br />資本政策表の作成経験がない担当者でも、簡単かつ正確に資本政策表が可能になり、属人的になりがちな作業を標準化できます。
      </p>
    </FeatureCard>
  );
};

export const FeatureCard5: React.FC<Pick<FeatureCardProps, 'className'>> = ({
  className,
}) => {
  return (
    <FeatureCard
      className={className}
      index={3}
      name="資本政策表作成機能"
      head={
        <>
          監査契約前に自社の内部統制状況がわかる！
        </>
      }
      textItems={[
        'ショートレビューセルフチェック機能',
      ]}
      imageSrc={withPrefix('/images/features/image-5.png')}
      result={
        <p>
          <strong>
            早期に体制構築に着手することで、<br css={css`
              @media ${getScreen('pc', '未満')} {
                display: none;
              }
            `}/>監査法人や証券会社から<br css={css`
              @media ${getScreen('pc', '未満')} {
                display: none;
              }
            `}/>「選ばれる会社 」に
          </strong>
        </p>
      }
      items={[
        {
          name: 'ショートレビュー\n課題管理機能',
          imageSrc: withPrefix('/images/features/icon-8.png'),
        },
      ]}
      pcPosition="right"
    >
      <p>
        ショートレビューで明確になった課題に対して対応が遅れると監査法人との契約が遅れる可能性もあります。Uniforceでは、ショートレビュー機能を活用して、事前に対策を練ることができる他、課題管理機能で契約遅延のリスクを最小限に抑えることが可能です。
      </p>
    </FeatureCard>
  );
};

export const FeatureCard4: React.FC<Pick<FeatureCardProps, 'className'>> = ({
  className,
}) => {
  return (
    <FeatureCard
      className={className}
      index={4}
      name="AIサポート機能（β版）"
      head={
        <>
          緊急度の高い人事労務の<br />課題も簡単に把握！！
        </>
      }
      textItems={[
        '人事労務セルフチェック機能',
      ]}
      imageSrc={withPrefix('/images/features/image-4.png')}
      result={
        <p>
          <strong>
            専門性の高い人事労務領域も<br />社内で課題把握が可能！！
          </strong>
        </p>
      }
      items={[
        {
          name: '人事労務\nセルフチェック',
          imageSrc: withPrefix('/images/features/icon-12.png'),
        },
      ]}
      pcPosition="left"
    >
      <p>
        人事労務管理はIPO準備を進める上で避けては通れません。今までの管理体制によっては、過去を遡って精算しなければならないケースも。時間がかかる上、労働基準法の専門知識が必要です。<br />このような優先度の高い箇所の課題特定と解決を早期に行うことが可能です。
      </p>
    </FeatureCard>
  );
};

export const FeatureCard3: React.FC<Pick<FeatureCardProps, 'className'>> = ({
  className,
}) => {
  return (
    <FeatureCard
      className={className}
      index={5}
      name="ショートレビュー対策機能"
      head={
        <>
          イグジット戦略に合わせた<br />最適なガバナンス構築が可能！
        </>
      }
      textItems={[
        '内部統制管理・運用機能'
      ]}
      imageSrc={withPrefix('/images/features/image-3.png')}
      result={
        <p>
          <strong>
            過剰なガバナンス構築を防ぎ、<br />企業成長と体制構築を両立！
          </strong>
        </p>
      }
      items={[
        {
          name: '内部統制構築',
          imageSrc: withPrefix('/images/features/icon-4.png'),
        },
        {
          name: 'タスク\nガイドライン',
          imageSrc: withPrefix('/images/features/icon-2.png'),
        },
        {
          name: '内部統制QA\n（品質管理）',
          imageSrc: withPrefix('/images/features/icon-6.png'),
        },
      ]}
      pcPosition="right"
    >
      <p>
        形式的・表面的に構築した内部統制では、事業側の実務に沿った運用が行えずに企業成長を妨げてしまったり、不祥事の発生リスクを低下させることはできません。Uniforceでは事業部側を巻き込んだプロジェクト体制を前提とした設計で、実態に伴ったカバナンスの構築で企業価値を高める支援を行います。
      </p>
    </FeatureCard>
  );
};


export const FeatureCard2: React.FC<Pick<FeatureCardProps, 'className'>> = ({
  className,
}) => {
  return (
    <FeatureCard
      className={className}
      index={6}
      name="内部統制アジャイル機能"
      head={
        <>
          困った時はすぐにUniforceのアドバイザーに相談が可能！
        </>
      }
      textItems={[
        '困ったときの相談窓口機能',
      ]}
      imageSrc={withPrefix('/images/features/image-2.png')}
      result={
        <p>
          <strong>
            誰に相談すればいいかわからない！<br />そんな状況をUniforceが解決！
          </strong>
        </p>
      }
      items={[]}
      pcPosition="left"
    >
      <p>
        IPOを推進する現場では、「社内にIPO経験者がいないため、相談相手がいない」ことが多く、「誰に、どのように」相談すればいいか？もわからない実態があります。IPO準備クラウドでは「現在困っている業務や悩み」から専門家への相談を手軽に行うことができる窓口で皆さまを強力にサポートしています。
      </p>
    </FeatureCard>
  );
};

export const FeatureCard7: React.FC<Pick<FeatureCardProps, 'className'>> = ({
  className,
}) => {
  return (
    <FeatureCard
      className={className}
      index={7}
      name="内部統制アジャイル機能"
      head={
        <>
          ストックオプションの<br />複雑な管理業務をサポート
        </>
      }
      textItems={[
        <>
          ストックオプション機能 <sup>※</sup>
        </>,
      ]}
      imageSrc={withPrefix('/images/features/image-7.png')}
      result={
        <>
          <p>
            <strong>
              ストックオプション管理における不備を未然に防ぐことが可能です。
            </strong>
          </p>
          <p className="option">※本機能はオプション機能のため、別途契約が必要です。</p>
        </>
      }
      items={[
        {
          name: 'ストックオプション機能',
          imageSrc: withPrefix('/images/features/icon-8.png'),
        },
      ]}
      pcPosition="right"
    >
      <p>
        ストックオプションの管理業務は複雑で、不十分な管理は監査法人や証券会社からの指摘事項となるケースも多く、企業の上場準備に大きな影響を及ぼす可能性があります。本機能では、関連資料の一元管理や、Excel管理では実現しづらい整合性チェックによって、ストックオプション管理における工数削減や人為的ミスの防止に貢献します。
      </p>
    </FeatureCard>
  );
};

